/**
 * Generated Code Viewer — Displays generated test automation code.
 *
 * Shows a file tree on the left and syntax-highlighted code on the right,
 * with line numbers, download and copy functionality.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Description as FileIcon,
  Folder as FolderIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Code as CodeIcon,
  Settings as ConfigIcon,
  BugReport as TestIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { GeneratedCodePayload, GeneratedCodeFile, GeneratedCodeSummary } from '../types';

// Map our language labels to Prism grammar names.
const PRISM_LANG: Record<string, string> = {
  python: 'python', typescript: 'typescript', javascript: 'javascript',
  yaml: 'yaml', ini: 'ini', json: 'json', text: 'text',
};

interface GeneratedCodeViewerProps {
  code: GeneratedCodePayload;
  /** Paths to flag in the tree — e.g. files the current run touched. */
  highlightPaths?: string[];
  /** Tooltip/label for the flag. */
  highlightLabel?: string;
  /**
   * Fill the available height instead of capping at the inline-card size.
   *
   * This component was written to sit inside a chat message, where a 520px
   * ceiling is right. Rendered as a full-height tab that ceiling shows ~23
   * lines of a 218-line file with dead space beneath it.
   */
  fullHeight?: boolean;
  /** Header text. Defaults to the generated-code wording. */
  title?: string;
}

// Language to display name
const LANG_LABELS: Record<string, string> = {
  python: 'Python',
  yaml: 'YAML',
  ini: 'INI',
  text: 'Text',
  typescript: 'TypeScript',
  javascript: 'JavaScript',
};

// Colors for dark-theme syntax highlighting
const SYNTAX = {
  keyword: '#c586c0',    // purple — import, def, class, if, for, etc.
  builtin: '#dcdcaa',    // yellow — function calls, decorators
  string: '#ce9178',     // orange — strings
  comment: '#6a9955',    // green — comments
  number: '#b5cea8',     // light green — numbers
  decorator: '#dcdcaa',  // yellow — @pytest.mark etc.
  constant: '#4fc1ff',   // cyan — True, False, None
  normal: '#d4d4d4',     // light grey — default text
  lineNum: '#858585',    // dim grey — line numbers
  bg: '#1e1e1e',         // VS Code dark background
  gutterBg: '#252526',   // slightly lighter for gutter
};

const PYTHON_KEYWORDS = new Set([
  'import', 'from', 'def', 'class', 'return', 'if', 'elif', 'else',
  'for', 'while', 'try', 'except', 'finally', 'with', 'as', 'yield',
  'raise', 'pass', 'break', 'continue', 'and', 'or', 'not', 'in',
  'is', 'lambda', 'assert', 'global', 'nonlocal', 'del', 'async', 'await',
]);

const PYTHON_CONSTANTS = new Set(['True', 'False', 'None', 'self']);

const countTests = (content: string): number =>
  (content.match(/^\s*(?:async\s+)?def\s+test_/gm) || []).length;

/**
 * Basic Python syntax highlighter — tokenizes a single line and returns
 * spans with appropriate colors.
 */
function highlightPythonLine(line: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  const push = (text: string, color: string) => {
    nodes.push(<span key={key++} style={{ color }}>{text}</span>);
  };

  // Full-line comment
  const trimmed = line.trimStart();
  if (trimmed.startsWith('#')) {
    const indent = line.length - trimmed.length;
    if (indent > 0) push(line.slice(0, indent), SYNTAX.normal);
    push(trimmed, SYNTAX.comment);
    return nodes;
  }

  // Decorator line
  if (trimmed.startsWith('@')) {
    const indent = line.length - trimmed.length;
    if (indent > 0) push(line.slice(0, indent), SYNTAX.normal);
    push(trimmed, SYNTAX.decorator);
    return nodes;
  }

  while (i < line.length) {
    const ch = line[i];

    // String literals (single or double quotes, including triple quotes)
    if (ch === '"' || ch === "'") {
      const triple = line.slice(i, i + 3);
      const isTriple = triple === '"""' || triple === "'''";
      const quote = isTriple ? triple : ch;
      const end = line.indexOf(quote, i + quote.length);
      if (end >= 0) {
        const str = line.slice(i, end + quote.length);
        push(str, SYNTAX.string);
        i = end + quote.length;
      } else {
        // Unterminated — highlight rest of line as string
        push(line.slice(i), SYNTAX.string);
        i = line.length;
      }
      continue;
    }

    // Inline comment
    if (ch === '#') {
      push(line.slice(i), SYNTAX.comment);
      i = line.length;
      continue;
    }

    // Numbers
    if (/\d/.test(ch) && (i === 0 || /[\s(,=[\-+*/<>!]/.test(line[i - 1]))) {
      let j = i;
      while (j < line.length && /[\d.]/.test(line[j])) j++;
      push(line.slice(i, j), SYNTAX.number);
      i = j;
      continue;
    }

    // Words (identifiers, keywords)
    if (/[a-zA-Z_]/.test(ch)) {
      let j = i;
      while (j < line.length && /[a-zA-Z0-9_]/.test(line[j])) j++;
      const word = line.slice(i, j);
      if (PYTHON_KEYWORDS.has(word)) {
        push(word, SYNTAX.keyword);
      } else if (PYTHON_CONSTANTS.has(word)) {
        push(word, SYNTAX.constant);
      } else if (j < line.length && line[j] === '(') {
        push(word, SYNTAX.builtin);
      } else {
        push(word, SYNTAX.normal);
      }
      i = j;
      continue;
    }

    // Everything else (operators, whitespace, punctuation)
    push(ch, SYNTAX.normal);
    i++;
  }

  return nodes;
}

/** Get an icon for a file based on its path. */
function getFileIcon(path: string) {
  if (path.startsWith('tests/')) return <TestIcon sx={{ fontSize: 14 }} color="success" />;
  if (path.endsWith('.yml') || path.endsWith('.yaml') || path.endsWith('.ini'))
    return <ConfigIcon sx={{ fontSize: 14 }} color="action" />;
  return <FileIcon sx={{ fontSize: 14 }} color="action" />;
}

const GeneratedCodeViewer: React.FC<GeneratedCodeViewerProps> = ({ code, highlightPaths, highlightLabel, fullHeight, title }) => {
  const highlighted = React.useMemo(() => new Set(highlightPaths || []), [highlightPaths]);
  const [selectedFile, setSelectedFile] = useState<GeneratedCodeFile | null>(
    (code.files || []).length > 0 ? code.files[0] : null
  );
  const [copied, setCopied] = useState(false);
  const [fileQuery, setFileQuery] = useState('');

  // Parse summary — backend may send object or string
  const summary = useMemo((): GeneratedCodeSummary | null => {
    if (!code.summary) return null;
    if (typeof code.summary === 'object') return code.summary as GeneratedCodeSummary;
    return null;
  }, [code.summary]);

  const handleCopy = async () => {
    if (!selectedFile) return;
    try {
      await navigator.clipboard.writeText(selectedFile.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = selectedFile.content;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (code.download_url) {
      window.open(code.download_url, '_blank');
    }
  };

  // Compute line count for selected file
  const lineCount = useMemo(() => {
    if (!selectedFile) return 0;
    return selectedFile.content.split('\n').length;
  }, [selectedFile]);

  // Group files by directory
  const fileTree: Record<string, GeneratedCodeFile[]> = {};
  (code.files || []).forEach((file) => {
    const parts = file.path.split('/');
    const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '.';
    if (!fileTree[dir]) fileTree[dir] = [];
    fileTree[dir].push(file);
  });

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1, borderColor: 'divider', borderRadius: 2,
        ...(fullHeight
          ? { height: '100%', display: 'flex', flexDirection: 'column', mb: 0, overflow: 'hidden' }
          : { mb: 2 }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: '#252526',
          borderBottom: 1,
          borderColor: 'rgba(255,255,255,0.1)',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CodeIcon sx={{ color: '#569cd6', fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#d4d4d4' }}>
            {title || 'Generated Test Code'}
          </Typography>
          {summary && (
            <>
              <Chip
                size="small"
                label={`${summary.total_files || (code.files || []).length} files`}
                sx={{
                  height: 20, fontSize: '0.7rem',
                  bgcolor: 'rgba(255,255,255,0.08)', color: '#d4d4d4',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              />
              <Chip
                size="small"
                label={`${summary.total_test_cases || 0} tests`}
                sx={{
                  height: 20, fontSize: '0.7rem',
                  bgcolor: 'rgba(78,168,80,0.15)', color: '#4ec950',
                  border: '1px solid rgba(78,168,80,0.3)',
                }}
              />
            </>
          )}
          {(summary?.frameworks_used || []).map((fw: string) => (
            <Chip
              key={fw}
              size="small"
              label={fw}
              sx={{
                height: 20, fontSize: '0.7rem',
                bgcolor: 'rgba(86,156,214,0.15)', color: '#569cd6',
                border: '1px solid rgba(86,156,214,0.3)',
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {code.download_url && (
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Download ZIP
            </Button>
          )}
        </Box>
      </Box>

      {/* Content: File tree + Code viewer */}
      <Box sx={fullHeight
        ? { display: 'flex', flex: 1, minHeight: 0 }
        : { display: 'flex', minHeight: 320, maxHeight: 520 }}>
        {/* File Tree (left) — dark sidebar */}
        <Box
          sx={{
            width: 220,
            minWidth: 220,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            bgcolor: '#252526',
            overflowY: 'auto',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Search — a 31-file repo needs a filter */}
          <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: '#252526', zIndex: 1,
                     borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <TextField
              value={fileQuery}
              onChange={(e) => setFileQuery(e.target.value)}
              placeholder="Filter files…"
              size="small"
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: '#858585' }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '0.72rem', color: '#d4d4d4',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                },
              }}
            />
          </Box>
          <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
            {Object.entries(fileTree).map(([dir, allFiles]) => {
              const q = fileQuery.trim().toLowerCase();
              const files = q
                ? allFiles.filter((f) => f.path.toLowerCase().includes(q))
                : allFiles;
              if (files.length === 0) return null;
              return (
              <React.Fragment key={dir}>
                {dir !== '.' && (
                  <ListItem sx={{ py: 0.25, pl: 1 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <FolderIcon fontSize="small" sx={{ color: '#dcb67a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={dir.split('/').pop() || dir}
                      primaryTypographyProps={{
                        variant: 'caption',
                        fontWeight: 600,
                        noWrap: true,
                        sx: { color: '#cccccc' },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#6e7681', fontSize: '0.65rem', pr: 0.5 }}>
                      {files.length}
                    </Typography>
                  </ListItem>
                )}
                {files.map((file) => {
                  const nTests = countTests(file.content || '');
                  const fileName = file.path.split('/').pop() || file.path;
                  const isSelected = selectedFile?.path === file.path;
                  return (
                    <ListItem key={file.path} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedFile(file)}
                        sx={{
                          py: 0.25,
                          pl: dir !== '.' ? 3 : 1,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255,255,255,0.08)',
                          },
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.05)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          {getFileIcon(file.path)}
                        </ListItemIcon>
                        {highlighted.has(file.path) && (
                          <Box
                            title={highlightLabel || 'changed'}
                            sx={{
                              width: 6, height: 6, borderRadius: '50%',
                              bgcolor: '#4ade80', flexShrink: 0, mr: 0.75,
                            }}
                          />
                        )}
                        <ListItemText
                          primary={fileName}
                          primaryTypographyProps={{
                            variant: 'caption',
                            noWrap: true,
                            sx: {
                              fontSize: '0.75rem',
                              color: isSelected ? '#ffffff' : '#cccccc',
                            },
                          }}
                        />
                        {nTests > 0 && (
                          <Tooltip title={`${nTests} test${nTests === 1 ? '' : 's'}`}>
                            <Box sx={{
                              flexShrink: 0, ml: 0.5,
                              minWidth: 18, px: 0.5, height: 15, borderRadius: '7px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: 'rgba(78,168,80,0.18)', color: '#7ee787',
                              fontSize: '0.6rem', fontWeight: 700,
                              fontFamily: 'monospace',
                            }}>
                              {nTests}
                            </Box>
                          </Tooltip>
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </React.Fragment>
              );
            })}
          </List>
        </Box>

        {/* Code Viewer (right) — dark editor */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', bgcolor: SYNTAX.bg }}>
          {selectedFile ? (
            <>
              {/* File header bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 0.5,
                  bgcolor: '#2d2d2d',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#cccccc', fontWeight: 500 }}>
                    {selectedFile.path}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#858585' }}>
                    {lineCount} lines
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    size="small"
                    label={LANG_LABELS[selectedFile.language] || selectedFile.language}
                    sx={{
                      height: 18, fontSize: '0.6rem',
                      bgcolor: 'rgba(255,255,255,0.08)', color: '#cccccc',
                    }}
                  />
                  <Tooltip title={copied ? 'Copied!' : 'Copy file'}>
                    <IconButton size="small" onClick={handleCopy} sx={{ color: '#cccccc' }}>
                      {copied ? (
                        <CheckIcon fontSize="small" sx={{ color: '#4ec950' }} />
                      ) : (
                        <CopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Code content — real editor highlighting with a VS Code theme */}
              <Box sx={{ ...(fullHeight ? { flex: 1, minHeight: 0 } : { maxHeight: 460 }),
                         overflow: 'auto', bgcolor: SYNTAX.bg }}>
                <SyntaxHighlighter
                  language={PRISM_LANG[selectedFile.language] || 'python'}
                  style={vscDarkPlus}
                  showLineNumbers
                  wrapLongLines={false}
                  lineNumberStyle={{
                    color: SYNTAX.lineNum, minWidth: '2.5em', paddingRight: '1em',
                    userSelect: 'none', textAlign: 'right',
                  }}
                  customStyle={{
                    margin: 0, background: SYNTAX.bg, fontSize: '0.78rem',
                    lineHeight: 1.6, padding: '10px 4px',
                  }}
                  codeTagProps={{ style: {
                    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                  } }}
                >
                  {selectedFile.content}
                </SyntaxHighlighter>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" sx={{ color: '#858585' }}>
                Select a file to view
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default GeneratedCodeViewer;
